import { useEffect, useMemo, useState, useRef } from "react";
import { useContent } from "contexts/contentContext/contentContext";
import Button from "components/Elements/Button/Button";
import Icon from "components/Elements/Icon/Icon";
import { EMAIL_VALIDATOR } from "helpers/validators.js";
import SubmittedForm from "./SubmittedForm";
import useCountries from "components/Pages/Purchases_new/hooks/useCountries";
import { CountryT } from "components/Pages/Purchases_new/models/models";
import styles from "./adContactForm.module.scss";
import SelectElement from "components/Elements/SelectElement/SelectElement";
import { useUser } from "contexts/userContext/userContext";
import { useToasts } from "react-toast-notifications";
import Recaptcha from "components/Reusable/Recaptcha/Recaptcha";
import ReCAPTCHA from "react-google-recaptcha";
import Image from "components/Elements/Image/Image";

export type FormDataT = {
  name: string;
  email: string;
  location: string;
  company: string;
  budget: string;
  description: string;
};

type FormErrorsT = {
  [K in keyof Omit<FormDataT, "budget" | "description">]: boolean;
};

const MAX_DESCRIPTION_LENGTH = 300;
const MAX_NAME_LENGTH = 30;
const MAX_COMPANY_LENGTH = 30;
const budgetOptions = ["100K+", "50k-100k", "20k-50k", "10k-20k"];

const AdContactForm = () => {
  const { getTextByKey, getDirection, getLanguageCulture } = useContent();
  const RTLDirection = getDirection() === "rtl";
  const { countries, fetchCountries } = useCountries();
  const { getUserData, contactUs } = useUser();
  const { addToast } = useToasts();
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const [formData, setFormData] = useState<FormDataT>({
    name: "",
    email: "",
    location: getUserData()?.UserCountry || "",
    company: "",
    budget: "50k-100k",
    description: "",
  });
  const [country, setCountry] = useState(getUserData()?.UserCountry);
  const [isLoading, setIsLoading] = useState(false);

  const [isSubmitted, setIsSubmitted] = useState(false);

  const [errors, setErrors] = useState<FormErrorsT>({
    name: false,
    email: false,
    location: false,
    company: false,
  });

  const [showError, setShowError] = useState(false);
  const [emailError, setEmailError] = useState(false);

  const validateField = (name: keyof FormDataT, value: string): boolean => {
    switch (name) {
      case "email":
        return EMAIL_VALIDATOR.test(value);
      case "name":
        return value.trim() !== "" && value.length <= MAX_NAME_LENGTH;
      case "company":
        return value.trim() !== "" && value.length <= MAX_COMPANY_LENGTH;
      default:
        return value.trim() !== "";
    }
  };

  useEffect(() => {
    fetchCountries();
  }, [fetchCountries]);

  useEffect(() => {
    if (country) {
      setFormData((prev) => ({
        ...prev,
        location: country,
      }));
      // Clear location error when country is selected
      setErrors((prev) => ({
        ...prev,
        location: false,
      }));
      setShowError(false);
    }
  }, [country]);

  const countriesSelectList = useMemo(
    () => countries.map((country: CountryT) => country.countryKey),
    [countries]
  );

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;

    // Prevent input if exceeds max length
    if (
      (name === "description" && value.length > MAX_DESCRIPTION_LENGTH) ||
      (name === "name" && value.length > MAX_NAME_LENGTH) ||
      (name === "company" && value.length > MAX_COMPANY_LENGTH)
    ) {
      return;
    }

    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));

    // Clear error for the field when user starts typing
    if (name in errors) {
      setErrors((prev) => ({
        ...prev,
        [name]: false,
      }));
      setShowError(false);

      // Special handling for email
      if (name === "email") {
        setEmailError(false);
      }
    }
  };

  const validateForm = () => {
    const newErrors: FormErrorsT = {
      name: false,
      email: false,
      location: false,
      company: false,
    };
    let hasErrors = false;

    (Object.keys(formData) as Array<keyof FormDataT>).forEach((field) => {
      if (field !== "budget" && field !== "description") {
        const isValid = validateField(field, formData[field]);
        newErrors[field] = !isValid;
        if (!isValid) hasErrors = true;

        // Special handling for email
        if (field === "email" && !isValid) {
          setEmailError(true);
        }
      }
    });

    setErrors(newErrors);
    setShowError(hasErrors);
    return !hasErrors;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (validateForm()) {
      setIsLoading(true);
      try {
        const token = await recaptchaRef.current?.executeAsync();
        if (!token) {
          addToast(getTextByKey("global-recaptcha-text"), {
            appearance: "error",
          });
          setIsLoading(false);
          return;
        }

        const formattedMessage = `
        Location: ${formData.location} Company: ${formData.company} Budget: ${formData.budget} Description: ${formData.description}
        `.trim();

        const contactUsBody = {
          ContactEmail: formData.email,
          ContactSender: formData.name,
          ContactPhone: "",
          ContactTitle: "sales",
          ContactMessage: formattedMessage,
          ContactCulture: getLanguageCulture().split("-")[0],
          Culture: getLanguageCulture().toLowerCase(),
          MailType: "ContactUsMail",
          userIp: getUserData()?.User_IP,
          platform: getUserData()?.User_Platform,
          recaptchaResponse: token,
        };

        const response = await contactUs(contactUsBody);

        if (response.status === "success") {
          setIsSubmitted(true);
          addToast(getTextByKey("contact-form-success"), {
            appearance: "success",
          });
        } else {
          setIsLoading(false);
          addToast(
            getTextByKey(response.message) ||
              getTextByKey("contact-form-error"),
            {
              appearance: "error",
            }
          );
        }
      } catch (error) {
        setIsLoading(false);
        addToast(getTextByKey("contact-form-error"), {
          appearance: "error",
        });
      } finally {
        if (!isSubmitted) {
          setIsLoading(false);
        }
      }
    }
  };

  if (isSubmitted) {
    return <SubmittedForm formData={formData} />;
  }

  return (
    <div className={styles.formContainer} id="ad-contact-form-container">
      {isSubmitted ? (
        <SubmittedForm formData={formData} />
      ) : (
        <>
          <div className={styles.formTextContentWrap}>
            <h2>{getTextByKey("contact-form-title")}</h2>
            <p>{getTextByKey("contact-form-description-1")}</p>
            <p>{getTextByKey("contact-form-description-2")}</p>
          </div>

          <form onSubmit={handleSubmit} className={styles.form} noValidate>
            <div className={styles.formInputsContainer}>
              <div className={styles.formRow}>
                <div className={styles.inputGroup}>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    id="name"
                    className={errors.name ? styles.error : ""}
                    placeholder={getTextByKey("contact-form-name-label")}
                    maxLength={MAX_NAME_LENGTH}
                  />
                </div>
                <div className={styles.inputGroup}>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    id="email"
                    className={errors.email ? styles.error : ""}
                    placeholder={getTextByKey("contact-form-email-label")}
                  />
                  {emailError && (
                    <div className={styles.fieldError}>
                      {getTextByKey("invalid-email-text")}
                    </div>
                  )}
                </div>
              </div>

              <div className={styles.formRow}>
                <div
                  className={`${styles.inputGroup} ${
                    errors.location ? styles.locationError : ""
                  }`}
                >
                  <SelectElement
                    title={getTextByKey("contact-form-select-location-label")}
                    list={countriesSelectList}
                    item={country}
                    setItem={setCountry}
                  />
                </div>
                <div className={styles.inputGroup}>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    required
                    id="company"
                    className={errors.company ? styles.error : ""}
                    placeholder={getTextByKey("contact-form-company-label")}
                    maxLength={MAX_COMPANY_LENGTH}
                  />
                </div>
              </div>
            </div>

            <div className={styles.budgetSection}>
              <label>{getTextByKey("contact-form-budget-label")}</label>
              <div className={styles.budgetOptions}>
                {budgetOptions.map((budget) => (
                  <button
                    key={budget}
                    type="button"
                    className={`${styles.budgetButton} ${
                      formData.budget === budget ? styles.active : ""
                    }`}
                    onClick={() => setFormData((prev) => ({ ...prev, budget }))}
                  >
                    {budget}
                  </button>
                ))}
              </div>
            </div>

            <div className={styles.textareaGroup}>
              <div className={styles.textareaGroupText}>
                <p>{getTextByKey("contact-form-textarea")}</p>
                <p className={`${styles.charCount}`}>
                  {formData.description.length}/{MAX_DESCRIPTION_LENGTH}
                </p>
              </div>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={4}
              />
            </div>
            <Recaptcha recaptchaRef={recaptchaRef} />
            <div className={styles.formFooter}>
              <div
                className={`invalid-notification ${styles.errorMessage} ${
                  !showError ? styles.hidden : ""
                }`}
              >
                <Icon name="warning" red />
                {getTextByKey("default-input-invalid-message")}
              </div>

              <Button
                type="submit"
                className={`advertising-button ${styles.submitBtn}`}
                disabled={isLoading}
              >
                {getTextByKey("contact-form-submit-button-label")}
                {isLoading ? (
                  <>
                    <Image
                      name="preloader"
                      ext="gif"
                      className={styles.submitBtnIcon}
                    />
                  </>
                ) : (
                  <>
                    <Icon
                      name={"arrow-2"}
                      blue
                      iconClass={`${styles.submitBtnIcon} ${
                        RTLDirection ? styles.submitBtnIconRotate : ""
                      }`}
                    />
                  </>
                )}
              </Button>
            </div>
          </form>
        </>
      )}
    </div>
  );
};

export default AdContactForm;
